import Table, { TableBody, TableHeader } from '@ingka/table';
import Text from '@ingka/text';
import { useFormatWithLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import type { UpcomingDraws } from '@lotta/shared/types-lottery-schemas';
import { useTranslation } from 'react-i18next';
import { useGetUpcomingDrawsQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import Loading, { LoadingBall } from '@ingka/loading';
import styles from './UpcomingDrawsTable.module.scss';

const UpcomingTableRows: React.FC<{
  rows: UpcomingDraws;
  showSignUps: boolean;
}> = ({ rows, showSignUps }) => {
  const { dateWithLocale, numberWithLocale } = useFormatWithLocale();
  const { t } = useTranslation();

  return (
    <>
      {rows.map(({ drawNo, startDate, endDate, drawDate, signups }) => {
        return (
          <tr key={drawDate} data-testid="upcoming-draws-table-row">
            <td>
              <Text className={styles['row-header']}>
                {t('general-draw')} {drawNo}
              </Text>
            </td>
            <td>
              <Text>{dateWithLocale(startDate)}</Text>
            </td>
            <td>
              <Text>{dateWithLocale(endDate)}</Text>
            </td>
            <td data-testid="upcoming-draw-date">
              <Text>{dateWithLocale(drawDate)}</Text>
            </td>
            {showSignUps && (
              <td>
                <Text>{signups !== undefined ? numberWithLocale(signups) : '-'}</Text>
              </td>
            )}
          </tr>
        );
      })}
    </>
  );
};

export const UpcomingDrawsTable: React.FC<{
  retailUnit: string;
  lotteryId: string;
  showSignUps: boolean;
}> = ({ retailUnit, lotteryId, showSignUps }) => {
  const { t } = useTranslation();

  const { data, isLoading, error } = useGetUpcomingDrawsQuery({ retailUnit, lotteryId });

  if (isLoading) {
    return (
      <Loading text={`${t('general-loading')}`}>
        <LoadingBall />
      </Loading>
    );
  }

  if (error) {
    return <Text headingSize="m">{t('general-something-went-wrong')}</Text>;
  }

  const upcomingDraws = data?.upcomingDraws || [];

  return (
    <Table fullWidth inset>
      <TableHeader sticky={true}>
        <tr>
          <th>
            <Text bodySize="s">{t('general-name')}</Text>
          </th>
          <th>
            <Text bodySize="s">{t('general-start-date')}</Text>
          </th>
          <th>
            <Text bodySize="s">{t('general-end-date')}</Text>
          </th>
          <th>
            <Text bodySize="s">{t('completed-draws-table-draw-date')}</Text>
          </th>
          {showSignUps && (
            <th>
              <Text bodySize="s">{t('general-sign-ups')}</Text>
            </th>
          )}
        </tr>
      </TableHeader>
      <TableBody striped>
        <UpcomingTableRows rows={upcomingDraws} showSignUps={showSignUps} />
      </TableBody>
    </Table>
  );
};
